import React, { FC, memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Icon from '~ComponentsImproved/Icon';
import StampCardIcon from '~Assets/icons/stamp_card.svg';
import { useI18n } from '~Hooks';
import { GetUser_User_stampCards, GetUser_User_stamps } from '../__queries__';

const StampCardRow = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-top: 1px solid var(--colorBlueGray900);
  padding-top: 16px;
`;

const ProgressBarTrack = styled(Box)`
  height: 6px;
  border-radius: 3px;
  background-color: var(--colorBlueGray1000);
  overflow: hidden;
`;

const ProgressBarFill = styled(Box)<{ progress: number }>`
  height: 100%;
  border-radius: 3px;
  width: ${({ progress }) => Math.min(progress, 100)}%;
  background-color: var(--colorUIPurple);
  transition: width 0.3s ease;
`;

interface Props {
  stampCards: GetUser_User_stampCards[];
  stamps: GetUser_User_stamps[];
}

/** Displays the user's stamp cards with progress visualization */
const UserStampCards: FC<Props> = ({ stampCards, stamps }) => {
  const { i18n } = useI18n();

  // Calculate stamp counts per card
  const stampCountByCard = useMemo(() => {
    const counts: Record<string, number> = {};
    stamps.forEach((stamp) => {
      counts[stamp.stampCard.id] = (counts[stamp.stampCard.id] || 0) + 1;
    });
    return counts;
  }, [stamps]);

  if (!stampCards || stampCards.length === 0) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" fluid gap="xs">
      {stampCards.map((stampCard) => {
        const collected = stampCountByCard[stampCard.id] || 0;
        const total = stampCard.count;
        const progress = total > 0 ? (collected / total) * 100 : 0;

        return (
          <StampCardRow key={stampCard.id} display="flex" flexDirection="column" gap="xs" fluid>
            <Box display="flex" alignItems="center" justifyContent="space-between" fluid>
              <Box display="flex" alignItems="center" gap="xs">
                <Icon
                  src={StampCardIcon}
                  height="24px"
                  width="24px"
                  color="var(--colorPOSClassic7)"
                />
                <Paragraph level={4} bottom={0} color="var(--colorWhite)">
                  {i18n(stampCard.nameLang)}
                </Paragraph>
              </Box>

              <Paragraph level={5} bottom={0} color="var(--colorBlueGray300)">
                {collected} / {total}
              </Paragraph>
            </Box>

            <ProgressBarTrack fluid>
              <ProgressBarFill progress={progress} />
            </ProgressBarTrack>
          </StampCardRow>
        );
      })}
    </Box>
  );
};

export default memo(UserStampCards);
